// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe, Olli Powroznik
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : wiki parts
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Menu} from 'semantic-ui-react'
import {useDebounce} from 'use-debounce'
// ---Framework imports

import log from 'uix/logging'
import {Sel, Actions} from 'uix/registries'
import {get, size, isNil} from 'ldash'
import {formatDistance} from 'uix/utils'
import {Part} from 'uix/parts'
import {Editor, Differ, mimetype2options} from 'uix/components/editor'
import {XMenu, HeaderSegment, DebugFeature, SplitPane, Toolbar} from 'uix/components'
import {selectDomainClassCfgs} from 'uix/selectors'
import {useActions, useEffect} from 'uix/hooks'

// ---Local imports

import {saveWikiText, setEditedWikiCode} from '../actions'
import styles from '../wiki.scss'
import {
  WikiPreview,
  KeyMap,
  WikiMimetype,
  ChildResourceUpload,
  WikiAlternatives,
} from '../components'
import {WikiInfoPane} from '../codeinfo'
import {WebdavUpload} from '../webdav'
import {MIME} from '../utils'
import {buildCompleter, buildWikiTemplates} from '../hooks'

// ---Globals

const editTrackerStoreKey = ({otid, dname, identifier}) => otid || `${dname}_${identifier}`

const WikiEditTracker = ({otid, dname, identifier, text, updateTS}) => {
  let set = useActions(setEditedWikiCode)
  let [bouncedText] = useDebounce(text, 2000) // we force also on unmount?

  useEffect(() => {
    if (!isNil(bouncedText)) {
      set({
        text: bouncedText,
        storeKey: editTrackerStoreKey({otid, dname, identifier}),
        remoteUpdateTs: updateTS,
      })
    }
  }, [otid, dname, identifier, bouncedText, updateTS])
  return null
}

export class WikiEditor extends Part {
  static storeMap = (root, props) => ({
    ...Part.selectPartData(root, props),
    domainDescription: Sel.domainDescription(root),
    jobSpec: root.config.jobSpec,
    classCfg: selectDomainClassCfgs(root),
    wikiEdited: root.data.wiki.edited,
    widgetSchema: root.config.widgetSchema,
  })
  static actionMap = {
    saveWikiText,
    setEditedWikiCode,
    callApi: Actions.callApi,
  }

  static getChunkedUtils = ['EditorUtils']
  // for StandalonePart support
  static filters = [{type: 'getWikiCode', resolve: true, paramsFromPart: ['identifier', 'id']}]

  static partStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    // 100% needed in standalone dialog form?
    height: '100%',
    //~ overflowX: 'hidden',
  }

  static uiDefaults = {
    activeIndex: 0,
    // editor
    lineWrapping: true,
    // preview mode
    previewActive: true,
    editorInfoArea: false,
    uploadActive: false,
    webdavUploadActive: false,
    __version__: 12,
  }

  state = {
    editorMounted: false,
    identifier: null,
    otid: null,
    updateTS: null,
    editorCursorToken: null,
    diffActive: false,
  }

  constructor(props) {
    super(props)

    this.state = {...this.state, ...WikiEditor.getDerivedStateFromProps(props, this.state)}
    this.cm = null
  }
  static getStoreKey = props => props.partcfg.storeKey || `${props.type}_${props.name || 'main'}`

  static getDerivedStateFromProps(props, state) {
    let nextState = {}
    if (!props.wikiCode) {
      return nextState
    }
    let {language, wikiCode, ui, wikiEdited} = props
    let {missing, text, identifier, otid, updateTS, dname, mimetype, datatype} = wikiCode

    //~ if (!missing) {
    let identChanged = state.identifier !== identifier
    let idChanged = state.otid !== otid
    let tsChanged = state.updateTS !== updateTS
    let languageChange = state.language !== language

    if (identChanged || idChanged || tsChanged) {
      let resetEditor = identChanged || (state.otid !== null && idChanged)
      nextState = {
        ...nextState,
        identifier,
        otid,
        updateTS,
        source: text, // original source
        changesPending: false,
        changedSource: undefined, // changes in main editor
        editorCursor: resetEditor ? {line: 0, ch: 0} : state.editorCursor,
        editorScroll: resetEditor ? {x: 0, y: 0} : state.editorScroll,
        editorCursorToken: null,
        language,
      }
    } else if (languageChange) {
      nextState = {...nextState, language}
    }

    if (isNil(nextState.changedSource ?? state.changedSource)) {
      let lastEditOp = wikiEdited[editTrackerStoreKey({otid, dname, identifier})]
      if (lastEditOp && (!updateTS || lastEditOp.remoteUpdateTs >= updateTS)) {
        let changesPending = text != lastEditOp.text
        if (changesPending) {
          nextState = {...nextState, changedSource: lastEditOp.text, changesPending}
        }
      }
    }

    return nextState
  }

  mountEditor = (editor, width, height) => {
    this.cm = editor
    // trigger rerender, this.cm is now available
    this.setState({editorMounted: true})
  }

  saveWikiText = () => {
    let {wikiCode, dname} = this.props
    let {changesPending, changedSource} = this.state
    if (!changesPending) {
      // dont write empty changedSource docs
      log.warn('nothing to save')
      return
    }

    let args = {text: changedSource ?? '', partName: this.partName(), dname}
    if (wikiCode.otid) {
      args.otid = wikiCode.otid
    } else if (wikiCode.identifier) {
      args.identifier = wikiCode.identifier
    } else {
      log.warn('no otid or identifier')
      return
    }
    if (wikiCode.missing) {
      args.mimetype = wikiCode.mimetype
    }

    this.props.saveWikiText(args)
  }

  codeChange = ({value: text}) => {
    let {source} = this.state
    this.setState({changesPending: source !== text, changedSource: text})
  }

  cursorChange = ({editor, data}) => {
    let editorCursorToken = editor.getTokenAt(editor.getCursor())
    this.setState({editorCursor: data, editorCursorToken})
  }

  editorScrollChange = ({editor, data}) => {
    this.setState({editorScroll: data})
  }

  partName = () => this.props.partcfg.storeKey || this.props.name || this.props.type

  onSaveKey = () => {
    this.saveWikiText()
  }

  renderEditor() {
    let {ui, setUI, EditorUtils, classCfg, jobSpec, wikiCode, widgetSchema} = this.props
    let {
      source,
      changedSource,
      editorCursor,
      editorScroll,
      identifier,
      editorCursorToken,
      diffActive,
    } = this.state
    let {otid, mimetype} = wikiCode
    let {mode, options: modeOptions} = mimetype2options(mimetype)

    // completer funcs can consume these configs
    let env = {classCfg, jobSpec}
    let completion = buildCompleter(buildWikiTemplates(widgetSchema), EditorUtils, env, mimetype)

    let opts = {
      mode,
      lineWrapping: ui.lineWrapping,
      autofocus: false, // moving in tree should not steal focus (XXX: does not work)
      lineNumbers: wikiCode.datatype === 18, // code datatype should have line numbers
      showTrailingSpace: true,
      tabSize: 2,
      hintOptions: completion.hintOptions,
    }

    let extraKeys = {
      'Ctrl-S': this.onSaveKey,
      ...completion.extraKeys,
    }

    //~ classNames={[styles.editorTopPane, styles.editorBottomPane]}
    let wrapWithInfo = editor => (
      <SplitPane
        persist
        name="InfoSplitPane"
        sizes={[0.8, 0.2]}
        styles={[{overflow: 'hidden', display: 'flex'}, {overflowX: 'hidden'}]}
        borderless={ui.previewActive}
      >
        {editor}
        <div style={{overflow: 'auto'}}>
          <WikiInfoPane
            key={`${wikiCode.identifier}_${otid}`}
            cmMode={mode}
            identifier={wikiCode.identifier}
            changedTs={wikiCode.updateTS}
            cmToken={editorCursorToken}
          />
        </div>
      </SplitPane>
    )

    let editor = null
    if (!diffActive) {
      editor = (
        <>
          {!isNil(changedSource) ? <WikiEditTracker {...wikiCode} text={changedSource} /> : null}
          <Editor
            flex
            mimetype={mimetype}
            bordered={!ui.previewActive && !ui.editorInfoArea}
            text={changedSource ?? source}
            opts={opts}
            extraKeys={extraKeys}
            onBeforeChange={this.codeChange}
            editorDidMount={this.mountEditor}
            onCursor={this.cursorChange}
            cursor={editorCursor}
            onScroll={this.editorScrollChange}
            scroll={editorScroll}
            onContextMenu={() => {}}
          />
        </>
      )
    } else {
      editor = (
        <Differ
          flex
          textLeft={source}
          textMiddle={changedSource ?? ''}
          mode={mode.name}
          xml={modeOptions.xml}
          lineNumbers={opts.lineNumbers}
          lineWrapping={opts.lineWrapping}
          onChange={this.codeChange}
        />
      )
    }

    if (ui.editorInfoArea) {
      editor = wrapWithInfo(editor)
    }

    if (!ui.previewActive) {
      return editor
    }

    return (
      <SplitPane vertical persist name="PreviewSplitPane" flexInner>
        <>
          <Toolbar attached="top" size="small">
            {this.localEditorMenu()}
          </Toolbar>
          {editor}
        </>
        <WikiPreview
          source={changedSource ?? source}
          otid={otid}
          identifier={identifier}
          datataype={wikiCode.datatype}
          mimetype={wikiCode.mimetype}
          showMenu
        />
      </SplitPane>
    )
  }

  webdavUploadAvailable = () => this.props.wikiCode?.wikihub

  prettyPrint() {
    let text = this.getCurrentText()
    let {wikiCode} = this.props
    this.props.callApi(
      'prettyPrint',
      {text, mimetype: wikiCode.mimetype},
      ({prettyData}) => {
        this.setState({changedSource: prettyData, changesPending: prettyData != wikiCode.text})
      },
      {method: 'post'}
    )
  }
  localEditorMenu = () => {
    let {ui, setUI, EditorUtils, wikiCode} = this.props
    let {mimetype} = wikiCode

    return (
      <>
        <XMenu.Item
          active={ui.lineWrapping}
          onClick={() => setUI({lineWrapping: !ui.lineWrapping})}
          icon="linewrap"
          title="Textwrap"
        />
        <XMenu.Item
          onClick={() => {}}
          icon="keyboard outline"
          confirm={<KeyMap keymap={EditorUtils.getKeyMap()} />}
        />
        <XMenu.Item
          disabled={!this.cm}
          active={ui.editorInfoArea}
          onClick={() => setUI({editorInfoArea: !ui.editorInfoArea})}
          icon="help"
          title="Show Infopane"
        />
        <XMenu.Item
          position="right"
          disabled={mimetype == MIME.MARKDOWN || mimetype == MIME.HTML}
          onClick={() => this.prettyPrint()}
          icon="prettyprint"
          title="Pretty Print"
        />
      </>
    )
  }

  editorMenu = mode => {
    let {ui, setUI, wikiCode, go, setEditedWikiCode, domainDescription, dname} = this.props
    let {changesPending, otid, diffActive} = this.state
    let cm = this.cm
    let {mimetype} = wikiCode
    let revert = () => {
      setEditedWikiCode({text: undefined, storeKey: editTrackerStoreKey(wikiCode)})
      this.setState({changesPending: false, changedSource: undefined})
    }

    let confirmNew = (
      <span>
        {'Create new wikipage '}
        <b>
          <tt>{wikiCode.identifier}</tt>
        </b>
        {' in domain: '}
        <b>{domainDescription}</b>
      </span>
    )
    return (
      <XMenu>
        <XMenu.Item
          icon="save"
          disabled={!changesPending}
          onClick={() => this.saveWikiText()}
          confirm={wikiCode.otid ? undefined : confirmNew}
          title="Save (Strg+S)"
        />
        {this.webdavUploadAvailable() && (
          <XMenu.Item
            icon="upload"
            active={ui.webdavUploadActive}
            onClick={() => setUI({webdavUploadActive: !ui.webdavUploadActive})}
            title="Upload to help.vls.io"
          />
        )}
        <XMenu.Item
          icon={['undo', {name: 'lightning', color: 'red'}]}
          disabled={!changesPending}
          onClick={revert}
          title="Revert"
          confirm="Revert all changes to last saved state?"
        />
        <XMenu.Item
          disabled={!changesPending}
          active={diffActive}
          onClick={() => this.setState({diffActive: !diffActive})}
          icon="exchange"
          title="Diff"
        />
        {otid && (
          <XMenu.Item
            active={ui.uploadActive}
            onClick={() => setUI({uploadActive: !ui.uploadActive})}
            icon="upload"
            title="Upload resources"
          />
        )}
        <XMenu.Separator />
        {!ui.previewActive && this.localEditorMenu()}
        <XMenu.Item
          disabled={!cm}
          active={ui.previewActive}
          onClick={() => setUI({previewActive: !ui.previewActive})}
          icon="columns"
          title="Preview"
        />
        <DebugFeature>
          <XMenu.SubMenu icon="cog" content="More">
            <WikiMimetype
              icon="configure"
              otid={wikiCode.otid}
              datatype={wikiCode.datatype}
              mimetype={mimetype}
              onClick={() => {}}
            />
            {wikiCode.fspath && <XMenu.Item content={wikiCode.fspath} />}
            <XMenu.Item
              onClick={() => go({classname: 'tree', tabname: 'id', id: wikiCode.otid})}
              icon="link"
              disabled={!wikiCode.otid}
              content={`Goto ${wikiCode.otid}`}
            />
          </XMenu.SubMenu>
        </DebugFeature>
      </XMenu>
    )
  }

  webdavUpload = () => {
    let {ui, wikiCode} = this.props
    if (!this.webdavUploadAvailable() || !ui.webdavUploadActive) return null
    return (
      <WebdavUpload
        identifier={wikiCode.identifier}
        mimetype={wikiCode.mimetype}
        text={this.getCurrentText()}
      />
    )
  }

  getCurrentText() {
    return this.state.changedSource || this.props.wikiCode.text
  }

  renderSegment() {
    let {ui, setUI, wikiCode, partcfg, request} = this.props

    let subheader = null
    let content = null
    let menu = null

    if (wikiCode) {
      content = (
        <>
          {this.webdavUpload()}
          {this.renderEditor()}
        </>
      )

      let {otid, identifier} = wikiCode

      if (otid && wikiCode.updateTS) {
        subheader = `changed: ${formatDistance(wikiCode.updateTS)} ago`
      }
      menu = this.editorMenu('sui')
    }

    return (
      <HeaderSegment
        major
        loading={!wikiCode}
        flex
        icon="edit"
        header="Editor"
        subheader={subheader}
        menu={menu}
        fullscreenable
      >
        {content}
      </HeaderSegment>
    )
  }
  _render() {
    let {partcfg, ui} = this.props
    let {otid} = this.state
    if (!partcfg.bare) {
      return (
        <>
          {this.renderSegment()}
          {partcfg.showAlternatives && <WikiAlternatives />}
          {ui.uploadActive && <ChildResourceUpload otid={otid} />}
        </>
      )
    } else {
      return this.renderEditor()
    }
  }
}

//~ export default WikiEditor
